/**
 * A room's "..." in Season: a sheet of what the room offers (the same items as the computer's menu), the confirm
 * sheets for delete and unpublish, and the invite sheet. A draft also offers Continue on the board.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { boardPath } from "../../board/paths";
import { studioPath } from "../../board/LiveStrip";
import { InviteSheet, inviteTargetOf } from "../../components/InviteSheet";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import type { MenuItem } from "../../components/ui/Menu";
import { Sheet } from "../../components/ui/Sheet";
import { useToast } from "../../components/ui/Toast";
import { copyRoomLink, deleteWords, type RoomForActions, roomMenuItems, useRoomActions, CHANGE_TIME_STATE } from "./RoomActions";
import { tabPath } from "./roomWords";

export function RoomMore({
  room: w,
  layout,
  onChanged,
  onDeleted,
  onChangeTime,
  onTab,
}: {
  room: RoomForActions;
  layout: SeasonLayout;
  onChanged: () => void;
  onDeleted?: () => void;
  /** On the room's own page, the time is changed right there. */
  onChangeTime?: () => void;
  onTab?: (tab: "page" | "sessions") => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<"delete" | "unpublish" | null>(null);
  const [inviting, setInviting] = useState(false);
  const draft = w.status === "draft";
  const { busy, remove, unpublish } = useRoomActions(w, { onChanged, onDeleted, onDone: () => setAsking(null) });
  const onAir = !!w.live && w.live.state !== "ended" && !w.live.series?.ended;

  const items = roomMenuItems(w, {
    toTab: (tab) => (onTab ? onTab(tab) : navigate(tabPath(w.id, tab))),
    changeTime: () => (onChangeTime ? onChangeTime() : navigate(`/rooms/${w.id}`, { state: CHANGE_TIME_STATE })),
    walk: () => navigate(boardPath(w.id)),
    invite: () => setInviting(true),
    unpublish: () => setAsking("unpublish"),
    askDelete: () => setAsking("delete"),
    copy: () => void copyRoomLink(w.register_url, toast),
    busy,
    // Why: "Edit the page" is only offered on the iPad on its side; the other layouts open the page and offer.
    ...(layout === "side" ? {} : { editLabel: "Page and offer" }),
    ...(onAir ? { studio: () => navigate(studioPath(w.id)) } : {}),
  });
  const rows: MenuItem[] = [
    ...(draft && w.board ? [{ label: "Continue on the board", icon: "list" as const, onSelect: () => navigate(boardPath(w.id)) }] : []),
    ...items.filter((i): i is MenuItem => i !== "sep"),
  ];
  const words = deleteWords(draft);

  return (
    <>
      <button type="button" className="k-ib" aria-label={`More for ${w.title}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <Icon name="more" />
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={w.title}>
        <GroupedList>
          {rows.map((r) => (
            <ListRow
              key={r.label}
              icon={r.icon ?? "list"}
              title={r.label}
              {...(r.danger ? { tone: "danger" as const } : {})}
              onClick={() => {
                setOpen(false);
                r.onSelect();
              }}
            />
          ))}
        </GroupedList>
      </Sheet>
      <Sheet
        open={asking === "delete"}
        onClose={() => setAsking(null)}
        title={words.title}
        sub={w.title}
        footer={
          <>
            <Button onClick={() => setAsking(null)}>Keep it</Button>
            <Button variant="p" className="dangerb" icon="trash" onClick={remove} disabled={busy}>
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p className="rm-p">{words.text}</p>
      </Sheet>
      <Sheet
        open={asking === "unpublish"}
        onClose={() => setAsking(null)}
        title="Unpublish this room?"
        sub={w.title}
        footer={
          <>
            <Button onClick={() => setAsking(null)}>Keep it</Button>
            <Button variant="p" icon="pause" onClick={unpublish} disabled={busy}>
              {busy ? "Unpublishing…" : "Unpublish"}
            </Button>
          </>
        }
      >
        <p className="rm-p">Unpublishing hides the page. People who registered keep their page.</p>
      </Sheet>
      {inviting ? (
        <InviteSheet
          webinar={inviteTargetOf(w)}
          onClose={() => setInviting(false)}
          onSent={(send) => toast(`Sending ${send.total.toLocaleString("en-US")} ${send.total === 1 ? "invite" : "invites"}`)}
          onToast={toast}
        />
      ) : null}
    </>
  );
}
